import React from 'react';

/**
 * RiskDonut - SVG donut chart showing the distribution of session integrity status.
 * I-P0-B: buckets are the server integrity status (NONE / MEDIUM / HIGH) plus
 * NOT_EVALUATED for sessions that pre-date the server pipeline. These are
 * unreviewed review signals, not verdicts.
 *
 * @param {object} props
 * @param {object} props.distribution - shape: {NONE, MEDIUM, HIGH, NOT_EVALUATED}.
 * @param {number} [props.size=180]   - svg size (px).
 */
const SEGMENTS = [
  { key: 'HIGH',          label: 'High review signals',   color: '#f97316' },
  { key: 'MEDIUM',        label: 'Medium review signals', color: '#f59e0b' },
  { key: 'NONE',          label: 'No review signals',     color: '#22c55e' },
  { key: 'NOT_EVALUATED', label: 'Not evaluated',         color: '#94a3b8' },
];

export default function RiskDonut({ distribution = {}, size = 180 }) {
  const total = SEGMENTS.reduce((sum, s) => sum + (distribution[s.key] || 0), 0);
  const cx = size / 2;
  const cy = size / 2;
  const r  = size * 0.4;
  const inner = r * 0.62;

  // Build arc segments. If total is 0 we still render the empty ring.
  let cumulativeFraction = 0;
  const arcs = total === 0
    ? [{ ...SEGMENTS[3], startA: 0, endA: Math.PI * 2 - 0.0001, count: 0, fraction: 1 }]
    : SEGMENTS.map((seg) => {
        const count    = distribution[seg.key] || 0;
        const fraction = count / total;
        const startA   = cumulativeFraction * Math.PI * 2 - Math.PI / 2;
        const endA     = (cumulativeFraction + fraction) * Math.PI * 2 - Math.PI / 2;
        cumulativeFraction += fraction;
        return { ...seg, startA, endA, count, fraction };
      }).filter(a => a.count > 0);

  function arcPath(startA, endA, large) {
    const x0 = cx + r * Math.cos(startA);
    const y0 = cy + r * Math.sin(startA);
    const x1 = cx + r * Math.cos(endA);
    const y1 = cy + r * Math.sin(endA);
    const xi1 = cx + inner * Math.cos(endA);
    const yi1 = cy + inner * Math.sin(endA);
    const xi0 = cx + inner * Math.cos(startA);
    const yi0 = cy + inner * Math.sin(startA);
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {arcs.map((a) => {
          const large = a.endA - a.startA > Math.PI ? 1 : 0;
          return (
            <path
              key={a.key}
              d={arcPath(a.startA, a.endA, large)}
              fill={total === 0 ? '#e2e8f0' : a.color}
              stroke="white"
              strokeWidth="1"
            />
          );
        })}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="22" fontWeight="700" fill="#0f172a">
          {total}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10" fill="#64748b">
          sessions
        </text>
      </svg>
      <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {SEGMENTS.map((s) => (
          <li key={s.key} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
            <span className="text-slate-600">{s.label}</span>
            <span className="text-slate-400 font-mono">{distribution[s.key] || 0}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
